import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useI18n } from "i18n";
import { CircularProgress } from "@remo-co/ui-core/src/components/CircularProgress";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Container } from "@remo-co/ui-core/src/components/Container";
import { useTicker } from "modules/dateTime";
import { usePermissions } from "modules/audioVideo/hooks/usePermissions";
import { useDevices } from "modules/audioVideo/hooks";
import { Box } from "@remo-co/ui-core/src/components/Box";
import {
  selectBroadcastStatus,
  selectDelay,
  selectIsMeStartBroadcast,
  selectIsTurnOnMicCamWhenStart,
  selectStartedAt,
} from "../../redux/selectors";
import useBroadcastActions from "../../hooks/useBroadcastActions";
import { BroadcastStatus } from "../../redux/constants";
import { styles } from "./styles";

interface Props {
  isEventManager: boolean;
}

export const BroadcastDialog = (props: Props) => {
  const { t } = useI18n(["manageEvent"]);
  const { isEventManager } = props;
  const startedAt = useSelector(selectStartedAt);
  const delay = useSelector(selectDelay);
  const { broadcastTransitionComplete, stopBroadcast } = useBroadcastActions();
  const currentUserStartedPresentation = useSelector(selectIsMeStartBroadcast);
  const hostDevicesEnabled = useSelector(selectIsTurnOnMicCamWhenStart);
  const {
    microphoneActive,
    cameraActive,
    setMicrophoneActive,
    setCameraActive,
  } = useDevices();
  const { setPresentationPermissions } = usePermissions();
  const currentTime = useTicker(
    startedAt !== undefined && delay !== undefined,
    1000,
  );
  const broadcastStatus = useSelector(selectBroadcastStatus);

  const countdown = useMemo(() => {
    if (startedAt === undefined || delay === undefined) {
      return 0;
    }
    return Math.round(Math.max(startedAt + delay - currentTime, 0) / 1000);
  }, [currentTime, delay, startedAt]);

  useEffect(() => {
    if (countdown === 1) {
      if (currentUserStartedPresentation) {
        setMicrophoneActive(hostDevicesEnabled);
        setCameraActive(hostDevicesEnabled);
        setPresentationPermissions({
          hostDevicesEnabled,
        });
      }
      broadcastTransitionComplete();
    }
  }, [
    countdown,
    broadcastTransitionComplete,
    currentUserStartedPresentation,
    hostDevicesEnabled,
    setPresentationPermissions,
    setMicrophoneActive,
    setCameraActive,
  ]);

  if (countdown === 0 || broadcastStatus !== BroadcastStatus.PREPARING) {
    return null;
  }

  return (
    <Box sx={styles.broadcastDialog}>
      {countdown > 0 && (
        <Box sx={styles.spinner}>
          <CircularProgress size={75} thickness={2} />
          <Box sx={styles.timer}>
            <Typography variant="h3">{countdown}</Typography>
          </Box>
        </Box>
      )}
      <Box sx={styles.title}>
        <Typography variant="h3">
          {t("manageEvent:presentation.about.start")}
        </Typography>
      </Box>
      <Box sx={styles.text}>
        <Typography variant="body1">
          {t("manageEvent:please.wrap.conversation")}
        </Typography>
      </Box>
      {(microphoneActive || cameraActive) && (
        <Box sx={styles.note}>
          <Typography variant="body1">
            {t("manageEvent:mic.cam.state.is.retained")}
          </Typography>
        </Box>
      )}
      <Container top="md">
        {isEventManager && (
          <Button
            fullWidth
            onClick={stopBroadcast}
            variant="warning"
            color="red"
            sx={styles.stopBroadcastButton}
          >
            {t("manageEvent:stop.presentation")}
          </Button>
        )}
      </Container>
    </Box>
  );
};
